// Centralised time formatting — Law OS is India-first, so every
// timestamp shown to the user is rendered in Asia/Kolkata with 12-hour AM/PM,
// regardless of the browser's locale or timezone.

const TZ = "Asia/Kolkata";

function _parse(input) {
    if (input === null || input === undefined || input === "") return null;
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return null;
    return d;
}

/** "08 Feb 2026, 04:50 PM" (IST). Returns "—" for invalid/blank input. */
export function fmtDateTime(input, opts = {}) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", {
        timeZone: TZ,
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        ...opts,
    });
}

/** "08 Feb 2026" (IST). */
export function fmtDate(input, opts = {}) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", {
        timeZone: TZ,
        day: "2-digit",
        month: "short",
        year: "numeric",
        ...opts,
    });
}

/** "08-02-2026" (IST) — Indian DD-MM-YYYY convention. Use this for all
 *  official listings/reports/PDF headers where an exact numeric date is
 *  required. Returns "—" for invalid/blank input. */
export function fmtDMY(input) {
    const d = _parse(input);
    if (!d) return "—";
    // en-GB gives DD/MM/YYYY, we swap the separator to `-` per user spec.
    return d.toLocaleDateString("en-GB", {
        timeZone: TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).replaceAll("/", "-");
}

/** "08-02-2026 16:50 IST" — DD-MM-YYYY + 24h clock in IST. */
export function fmtDMYTime(input) {
    const d = _parse(input);
    if (!d) return "—";
    const date = d.toLocaleDateString("en-GB", {
        timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric",
    }).replaceAll("/", "-");
    const time = d.toLocaleTimeString("en-GB", {
        timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false,
    });
    return `${date} ${time} IST`;
}

/** "04:50 PM" (IST). */
export function fmtTime(input, opts = {}) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", {
        timeZone: TZ,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        ...opts,
    });
}

/** "Sunday, 8 February 2026" (IST) — used as a day header. */
export function fmtDayLabel(input) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", {
        timeZone: TZ,
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

/** "08 Feb" — short, no year. */
export function fmtDateShort(input) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", { timeZone: TZ, day: "2-digit", month: "short" });
}

/** "Feb 2026" — month label for calendars. */
export function fmtMonthYear(input) {
    const d = _parse(input);
    if (!d) return "—";
    return d.toLocaleString("en-IN", { timeZone: TZ, month: "long", year: "numeric" });
}

/** "2026-02-08" IST key for day grouping. */
export function istDateKey(input) {
    const d = _parse(input);
    if (!d) return "";
    // en-CA gives ISO-style YYYY-MM-DD when combined with the IST timezone
    return d.toLocaleString("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
}

/** Convert a <input type="datetime-local"> string ("2026-02-08T16:50") to a
 *  UTC ISO string while interpreting the input as IST. Browsers' default
 *  conversion uses the local TZ which is wrong for our India-first product. */
export function istInputToIso(local) {
    if (!local) return null;
    const [datePart, timePart = "00:00"] = String(local).split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = timePart.split(":").map(Number);
    // Build the equivalent UTC instant: IST = UTC + 5:30
    const utcMillis = Date.UTC(y, m - 1, d, hh, mm) - (5 * 60 + 30) * 60 * 1000;
    return new Date(utcMillis).toISOString();
}

/** Convert an ISO string to a value usable by <input type="datetime-local">
 *  in IST ("2026-02-08T16:50"). */
export function isoToIstInput(iso) {
    const d = _parse(iso);
    if (!d) return "";
    const parts = d.toLocaleString("en-GB", {
        timeZone: TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    // en-GB → "08/02/2026, 16:50"
    const [date, time] = parts.split(", ");
    const [dd, mm, yyyy] = date.split("/");
    return `${yyyy}-${mm}-${dd}T${time}`;
}
